import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { getSetupLinkScope } from "@/api/orval/ageless-marketing-setup-link/ageless-marketing-setup-link";
import { isAuthorized } from "@/core/auth/auth";

import {
  isRejectedSetupTokenError,
  matchAgelessMarketingPublicPath,
  readAgelessMarketingSetupToken,
} from "./agelessMarketingPublicAccess";

export type AgelessMarketingSetupAccess = {
  /** True when the page is being viewed without a session. */
  readonly isPublicView: boolean;
  /** True while a supplied setup token is still being checked with the backend. */
  readonly isVerifyingSetupLink: boolean;
  /**
   * True when embed setup may be changed: a signed-in admin, or a visitor holding a setup
   * token the backend accepted. False for a visitor with no token or an expired one, which
   * is what surfaces the "an administrator must be signed in" notice.
   */
  readonly canEditEmbedSettings: boolean;
  /** The token to send with a login-free allowlist write, or null when signed in. */
  readonly setupToken: string | null;
};

/**
 * Resolves who is allowed to change website-embed settings on the Ageless Marketing page.
 *
 * A signed-in admin always may. A logged-out visitor may only when they arrived through a
 * setup link whose signed token still validates — the backend derives the platform from that
 * token, so the answer here is a UI affordance, never the security boundary.
 */
export function useAgelessMarketingSetupAccess(): AgelessMarketingSetupAccess {
  const location = useLocation();
  const isAuthed = isAuthorized();
  const routePlatformId = matchAgelessMarketingPublicPath(location.pathname);
  const isPublicView = !isAuthed && routePlatformId !== null;
  const setupToken = isPublicView ? readAgelessMarketingSetupToken(location.search) : null;

  const { data, isLoading } = useQuery({
    queryKey: ["ageless-marketing-setup-link", setupToken],
    // The token is a bearer credential: the mutator sends it whenever this page has no session,
    // so a 200 here means the link is good and a 401 means it is not.
    queryFn: () => getSetupLinkScope(),
    enabled: !!setupToken,
    retry: false,
    staleTime: Infinity,
    // An expired or forged token answers 401; that is "no access", not a page failure. Any other
    // failure (outage, rate limit, network) still reaches the route boundary rather than being
    // misreported as a missing token.
    useErrorBoundary: (error) => !isRejectedSetupTokenError(error),
  });

  // The token authorizes exactly one practice. Opened on another practice's route it must not
  // pass, or that route's inputs (a clinic location, say) would ride along onto the token's platform.
  const isScopeForRoute =
    !!data && !!routePlatformId && data.platformId.toLowerCase() === routePlatformId.toLowerCase();

  return {
    isPublicView,
    isVerifyingSetupLink: !!setupToken && isLoading,
    canEditEmbedSettings: isAuthed || (!!setupToken && isScopeForRoute),
    setupToken: isScopeForRoute ? setupToken : null,
  };
}
